'use client'

import { useState, useEffect } from 'react'
import CoursesSidebar from '@/components/content/courses/CourseSidebar'
import CourseOverview from '@/components/content/courses/CourseOverview'
import { App } from '@/payload-types'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'
import type { CourseType } from '@/app/(app)/fetchers'

type CoursesTypeProps = {
  courses: CourseType[]
}

type CoursePlaceholderProps = {
  children?: ReactNode
}

function CoursePlaceholder({ children }: CoursePlaceholderProps) {
  return (
    <div className="flex h-full w-[350px] shrink-0 grow flex-col gap-4 rounded-2xl border border-black/10 bg-white shadow-subtle">
      {children}
    </div>
  )
}

export default function CoursesType({ courses = [] }: CoursesTypeProps) {
  if (courses.length === 0) return null

  const [currentIndex, setCurrentIndex] = useState(0)
  const PLACEHOLDER_WIDTH = 350 + 24

  useEffect(() => {
    function changeIndex() {
      setCurrentIndex((prev) => {
        const next = prev + 1
        if (next >= courses.length) {
          return 0
        }
        return next
      })
    }

    changeIndex()

    // Set up interval for subsequent changes
    const interval = setInterval(() => {
      changeIndex()
    }, 5000)

    return () => clearInterval(interval)
  }, [courses.length])

  const singleCourse = courses.length === 1

  return (
    <div className="relative h-[280px] w-full overflow-hidden">
      <div
        className={cn(
          'absolute left-1/2 top-4 z-20 w-fit',
          singleCourse ? '-translate-x-1/2' : '-translate-x-[37.3%]',
        )}
      >
        <div
          className="flex h-[90px] gap-6"
          style={{
            transform: `translateX(${-currentIndex * PLACEHOLDER_WIDTH}px)`,
            transition: 'transform 0.5s ease-in-out',
          }}
        >
          <CoursePlaceholder />
          {courses.map((course, i) => {
            const app = course.app as App | undefined

            if (!app) return null

            return (
              <CoursePlaceholder key={i}>
                <div
                  className={cn(
                    'transition-opacity duration-500 ease-in-out',
                    i === currentIndex ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  <CoursesSidebar
                    course={course}
                    app={app}
                    customLink={`/courses/${app.slug}`}
                    hideBreadcrumb
                  />
                </div>
              </CoursePlaceholder>
            )
          })}
          <CoursePlaceholder />
        </div>
      </div>

      <div
        className={cn(
          'absolute right-1/2 top-[130px] flex w-fit',
          singleCourse ? 'translate-x-1/2' : 'translate-x-[37.3%]',
        )}
      >
        <div
          className="flex h-[180px] gap-6"
          style={{
            transform: `translateX(${currentIndex * PLACEHOLDER_WIDTH}px)`,
            transition: 'transform 0.5s ease-in-out',
          }}
        >
          <CoursePlaceholder />
          {courses.map((_, i) => {
            const visible = singleCourse ? i === currentIndex : i !== currentIndex
            return (
              <CoursePlaceholder key={i}>
                <div
                  className={cn(
                    'transition-opacity duration-500 ease-in-out',
                    visible ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  <CourseOverview borderless />
                </div>
              </CoursePlaceholder>
            )
          })}
          <CoursePlaceholder />
        </div>
      </div>
    </div>
  )
}
